import { useMemo, type ReactNode } from "react";

import { HandlerDesignerContext } from "./HandlerDesignerContext";
import { useTriggerDesigns } from "./TriggerDesignsContext";

type ProviderProps = {
  handlerId: number;
  triggerId: number;
  children: Exclude<ReactNode, null | undefined | boolean>;
};

export const HandlerDesignerContextProvider = ({ children, handlerId, triggerId }: ProviderProps) => {
  const {
    addResponse,
    getHandler,
    getNodeIdByTriggerId,
    getResponse,
    getResponses,
    getTrigger,
    removeResponse,
    reorderResponse,
    resolveConditionBuildables,
    resolveEffectBuildables,
    updateResponse,
  } = useTriggerDesigns();

  const trigger = getTrigger(triggerId);
  const handler = getHandler(handlerId);
  const triggerEventTypes = trigger.cases.map((c) => c.eventType);
  const eventTypes = handler.cases
    ? handler.cases.map((c) => c.eventType).filter((et) => triggerEventTypes.includes(et))
    : triggerEventTypes;

  const nodeId = useMemo(() => getNodeIdByTriggerId(triggerId), [getNodeIdByTriggerId, triggerId]);

  const providerProps = useMemo(
    () => ({
      id: handlerId,
      nodeId,
      eventTypes,
      getResponse,
      updateResponse,
      removeResponse,
      reorderResponse,
      getResponses: () => getResponses(handlerId),
      addResponse: () => addResponse(handlerId),
      resolveConditionBuildables,
      resolveEffectBuildables,
    }),
    [
      nodeId,
      addResponse,
      eventTypes,
      handlerId,
      getResponse,
      getResponses,
      removeResponse,
      reorderResponse,
      resolveConditionBuildables,
      resolveEffectBuildables,
      updateResponse,
    ],
  );

  return <HandlerDesignerContext.Provider value={providerProps}>{children}</HandlerDesignerContext.Provider>;
};
